import React, { useMemo } from "react";

/**
 * A minimalist, typography-based "word cloud" — not a circular bubble cloud.
 * Words sized by frequency. Uses monospace to signal data, not decoration.
 */

const STOP_WORDS = new Set([
  "the","a","an","and","or","but","for","of","to","in","on","at","by","with",
  "is","are","was","were","be","been","being","it","its","this","that","these",
  "those","we","you","they","our","their","there","as","if","than","so","not",
  "do","does","did","has","have","had","will","would","should","could","can",
  "just","too","very","more","less","some","any","all","no","yes","i","me","my",
  "he","she","him","her","his","hers","them","us","about","from","into","over",
  "under","through","between","across","also","only","even","much","many",
  "most","such","like","would","lot","said","say","think","feel","really",
  "people","one","two","three","things","thing","something","anything","nothing",
  "everyone","someone","somebody","anyone","who","what","when","where","why",
  "how","which","while","because","during","after","before","here","now","then",
  "up","down","out","off","other","others","another","each","own","same","get",
  "got","see","seen","go","going","make","makes","made","use","using","used",
  "need","needs","new","old","good","bad","big","small","very",
]);

function tokenise(text) {
  if (!text) return [];
  return text
    .toLowerCase()
    .replace(/[^a-z\s-]/g, " ")
    .split(/\s+/)
    .filter(Boolean);
}

function topWords(phrases, limit = 20) {
  const freq = new Map();
  for (const p of phrases) {
    for (const w of tokenise(p)) {
      if (w.length < 3) continue;
      if (STOP_WORDS.has(w)) continue;
      freq.set(w, (freq.get(w) || 0) + 1);
    }
  }
  return Array.from(freq.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit);
}

function scaleToClass(count, max) {
  const ratio = max ? count / max : 0;
  if (ratio > 0.66) return "text-xl font-semibold";
  if (ratio > 0.33) return "text-base font-medium";
  return "text-sm text-gray-700";
}

function CloudPanel({ title, subtitle, words, tone, testid }) {
  const max = words.reduce((m, [, c]) => Math.max(m, c), 0);
  const colorClass =
    tone === "positive"
      ? "text-emerald-800"
      : tone === "concern"
      ? "text-rose-800"
      : "text-slate-800";
  return (
    <div className="bg-white border border-gray-200 p-5" data-testid={testid}>
      <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider text-[11px]">
        {title}
      </h3>
      <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
      <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 items-baseline">
        {words.length === 0 ? (
          <span className="text-sm text-gray-400">No phrases extracted.</span>
        ) : (
          words.map(([w, c]) => (
            <span
              key={w}
              className={`${scaleToClass(c, max)} ${colorClass}`}
              title={`${w} · ${c}`}
            >
              {w}
              <span className="text-[10px] text-gray-400 font-mono ml-1">
                ·{c}
              </span>
            </span>
          ))
        )}
      </div>
    </div>
  );
}

export default function LanguageInsight({ workshops }) {
  const positiveWords = useMemo(() => {
    const phrases = workshops.flatMap((w) => w.positive_phrases || []);
    return topWords(phrases, 24);
  }, [workshops]);

  const concernWords = useMemo(() => {
    const phrases = workshops.flatMap((w) => w.cautionary_phrases || []);
    return topWords(phrases, 24);
  }, [workshops]);

  return (
    <section data-testid="language-insight">
      <div className="mb-4">
        <h2 className="text-xl font-semibold tracking-tight text-gray-900">
          Language insight
        </h2>
        <p className="text-sm text-gray-600 mt-1">
          Word frequency across the extracted positive and cautionary phrases.
          Size reflects how often the word appears across workshops.
        </p>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <CloudPanel
          title="Positive framing"
          subtitle="Language used when participants describe wind favourably"
          words={positiveWords}
          tone="positive"
          testid="cloud-positive"
        />
        <CloudPanel
          title="Constraint framing"
          subtitle="Language used when participants describe barriers or concerns"
          words={concernWords}
          tone="concern"
          testid="cloud-concern"
        />
      </div>
    </section>
  );
}
